import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import MainLayout from '../layouts/main';

// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';

// components
import LoadingScreen from '../components/LoadingScreen';

import { PATH_DASHBOARD_SEGMENTS } from './paths';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: '/',
      element: <MainLayout />,
      children: [
        { element: <HomePage />, index: true },
        { path: 'privacy', element: <Privacy />},
        { path: 'fb-delete-status', element: <FBDeleteStatus /> }
      ],
    },
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
      ],
    },
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/home" replace />, index: true },
        { path: 'home', element: <Dashboard /> },
        { path: 'leaderboards', element: <Leaderboards />},
        { path: 'messages', element: <Messages /> },
        { path: 'query-reports', element: <QueryReports /> }
      ],
    },
    {
      path: 'dashboard/network-rooms',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '', element: <NetworkRooms /> }
      ]
    },
    {
      path: 'dashboard/users',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '', element: <Users /> },
        { path: ':userId',
          element: <UserProfile />,
          children: [
            { index: true, element: <Navigate to={PATH_DASHBOARD_SEGMENTS.users.profile} replace /> },
            { path: PATH_DASHBOARD_SEGMENTS.users.profile, element: <UserProfileHome /> },
            { path: PATH_DASHBOARD_SEGMENTS.users.matchHistory, element: <UserProfileMatchHistory />},
            { path: PATH_DASHBOARD_SEGMENTS.users.inventory, element: <UserProfileInventory /> },
            { path: PATH_DASHBOARD_SEGMENTS.users.leaderboards, element: <UserProfileLeaderboards /> },
            { path: PATH_DASHBOARD_SEGMENTS.users.buddyChallenge, element: <UserProfileBuddyChallenge /> }
          ]
        },
      ],
    },
    {
      path: 'dashboard/buddy-challenge',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '', element: <BuddyChallenge /> }
      ],
    },
    {
      path: 'dashboard/purchases',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '', element: <Purchases /> }
      ],
    },
    {
      path: 'dashboard/events',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '', element: <Events /> },
        { path: ':eventId',
          children: [
            { index: true, element: <Navigate to="reports" replace /> },
            { path: 'reports/*', element: <EventReports /> },
          ]
        }
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/Login')));

// Dashboard
const Dashboard = Loadable(lazy(() => import('../pages/admin/Dashboard')));
const Leaderboards = Loadable(lazy(() => import('../pages/admin/Leaderboards')));
const NetworkRooms = Loadable(lazy(() => import('../pages/admin/NetworkRooms')));
const Messages = Loadable(lazy(() => import('../pages/admin/Messages')));
const BuddyChallenge = Loadable(lazy(() => import('../pages/admin/BuddyChallenge')));
const Purchases = Loadable(lazy(() => import('../pages/admin/Purchases')));
const QueryReports = Loadable(lazy(() => import('../pages/admin/QueryReports')));

const Events = Loadable(lazy(() => import('../pages/admin/Events')));
const EventReports = Loadable(lazy(() => import('../pages/admin/EventReports')));

// User Profile
const Users = Loadable(lazy(() => import('../pages/admin/Users')));
const UserProfile = Loadable(lazy(() => import('../pages/admin/UserProfile')));
const UserProfileHome = Loadable(lazy(() => import('../pages/admin/user_profile/UserProfileHome')));
const UserProfileMatchHistory = Loadable(lazy(() => import('../pages/admin/user_profile/UserProfileMatchHistory')));
const UserProfileInventory = Loadable(lazy(() => import('../pages/admin/user_profile/UserProfileInventory')));
const UserProfileLeaderboards = Loadable(lazy(() => import('../pages/admin/user_profile/UserProfileLeaderboards')));
const UserProfileBuddyChallenge = Loadable(lazy(() => import('../pages/admin/user_profile/UserProfileBuddyChallenge')));

// MAIN
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const Privacy = Loadable(lazy(() => import('../pages/Privacy')));

const NotFound = Loadable(lazy(() => import('../pages/Page404')));

// FB DELETE STATUS
const FBDeleteStatus = Loadable(lazy(() => import('../pages/FBDeleteStatus')));